import type {
  ConversationId, MessageId, ProfileId, SubjectId, TenantId, UserId,
} from './ids.js'
import type { ActionId } from './ids.js'

// ── Message content (discriminated union) ─────────────────────────────────────
// The runtime stays generic by never hard-coding business content: a message is
// one of a small, fixed set of shapes. `card`/`form`/`system` are how action
// results and structured prompts render — they subsume most "rich messaging"
// features without a per-feature content zoo.

export interface ProductItem { title: string; subtitle?: string; price?: string; image?: string; url?: string }

export interface CardField { label: string; value: string }

/** A reference to an action a card/quick-reply can invoke. */
export interface InlineActionRef { actionId: ActionId; label: string }

export type MessageContent =
  | { kind: 'text';        text: string; enc?: boolean; iv?: string; quickReplies?: string[]; citations?: { source: string }[] }
  | { kind: 'attachment';  url: string; mime: string; name?: string; size?: number }
  | { kind: 'card';        title?: string; body?: string; fields?: CardField[]; actions?: InlineActionRef[] }
  | { kind: 'form';        prompt: string; actionId: ActionId; prefill?: Record<string, string> }
  | { kind: 'system';      event: string; data?: Record<string, string | number | boolean> }
  | { kind: 'appointment'; title: string; startIso: string; endIso: string; location?: string; description?: string; googleUrl: string; icalUrl: string; confirmed?: boolean }
  | { kind: 'products';    title?: string; items: ProductItem[] }

export type SenderRole = 'guest' | 'agent' | 'system' | 'bot'

// ── Message ───────────────────────────────────────────────────────────────────
// Ordering is by `seq` (server-assigned, monotonic per conversation), never by
// `ts`. `ts` is wall-clock for display only. This kills the reorder/duplicate/
// lost-on-reconnect class of bugs that millisecond-timestamp ordering caused.

export interface Message {
  id:             MessageId
  conversationId: ConversationId
  seq:            number
  senderId:       UserId
  senderRole:     SenderRole
  content:        MessageContent
  ts:             number
  replyToId?:     MessageId
  editedAt?:      number
  deletedAt?:     number
  reactions?:     Record<string, UserId[]>
  internal?:      boolean   // true = internal note, only visible to agents
}

// ── Conversation (the room; messages partition by conversationId) ─────────────
// The room is the conversation, NOT the subject. Two guests discussing the same
// subject get two conversations. `subjectId` is a nullable reference, never part
// of the room identity — so "one thread per (guest, subject)" is enforced as
// app logic at open-time, and many-threads-per-subject stays possible for free.

export interface Conversation {
  id:              ConversationId
  tenantId:        TenantId
  profileId:       ProfileId            // behavior profile → which actions this room has
  subjectId?:      SubjectId            // optional: the thing it's about
  guestId:         UserId               // the end-user
  /** Host-supplied display info for an identified guest (widget `user` option).
   *  Display metadata only — identity is still the token/guestId. */
  guestName?:      string
  guestEmail?:     string
  guestAvatar?:    string
  guestMeta?:      Record<string, string>
  /** True once the guest's identity has been proven by a signed ES256 JWT
   *  against the chatroom's guestPublicKey (secure identity mode). */
  guestVerified?:  boolean
  participants:    UserId[]             // guest + any assigned agents (membership = authz)
  assignedAgentId?: UserId              // routing/ownership
  aiActive?:       boolean              // staff assigned the AI to answer this room
  state:           string               // conversation state-machine state
  firstResponseAt?: number              // first agent reply ts (SLA)
  csat?:           number               // satisfaction score 1–5 (set on resolution)
  lastSeq:         number               // highest seq assigned in this conversation
  tags?:           string[]             // macro/manual tags (e.g. "refund", "vip")
  /** Live sentiment of the guest's most recent message (best-effort, async). */
  sentiment?:      'positive' | 'neutral' | 'frustrated'
  /** -1 (very frustrated) .. +1 (very positive); paired with `sentiment`. */
  sentimentScore?: number
  /** Set once an SLA-breach escalation macro has fired, so it only fires once. */
  slaEscalatedAt?: number
  /** If set, the conversation is snoozed until this Unix ms timestamp.
   *  Hidden from the inbox until the timestamp passes, then resurfaces. */
  snoozedUntil?: number
  /** Page URL where the widget was open when the conversation started. */
  pageUrl?:        string
  /** Browser tab title at conversation start — gives agents context. */
  pageTitle?:      string
  createdAt:       number
  updatedAt:       number
}

// ── Subject (the referenced entity — Intercom "custom object") ────────────────
// Carries shared state (available → reserved → sold) and fields (price, vin…)
// that actions read/write. Many conversations reference one subject. Never a room.

export interface Subject {
  id:        SubjectId
  tenantId:  TenantId
  title:     string
  state:     string
  fields:    Record<string, string | number | boolean>
  /** URL of the page where the subject lives (e.g. the listing page URL).
   *  Captured automatically by the widget and stored on first open. */
  url?:      string
  createdAt: number
  updatedAt: number
}

// ── Conversation lifecycle ────────────────────────────────────────────────────
/** States in which a conversation is no longer "open": it's done with, so inbox
 *  / proactive sweeps skip it and load balancing frees the assigned agent. The
 *  single source of truth for "is this conversation finished?". */
export const TERMINAL_STATES: ReadonlySet<string> = new Set([
  'resolved', 'closed', 'sold', 'issued', 'checked_out',
])
export function isTerminalState(state: string): boolean {
  return TERMINAL_STATES.has(state)
}

export type Channel = 'widget' | 'email' | 'sms' | 'whatsapp' | 'instagram' | 'kakao' | 'messenger' | 'line'
